import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { Spinner } from './Spinner';
import { INTERACTIVE_CLASSES, VARIANT_CLASSES, type ButtonVariant } from './buttonStyles';

export type IconButtonSize = 'sm' | 'md' | 'lg';

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'> {
    variant?: ButtonVariant;
    size?: IconButtonSize;
    loading?: boolean;
    /** Required — an icon-only control must always have an accessible name. */
    'aria-label': string;
    children: ReactNode;
}

const SIZE_CLASSES: Record<IconButtonSize, string> = {
    sm: 'h-[22px] w-[22px]',
    md: 'h-[26px] w-[26px]',
    lg: 'h-8 w-8',
};

const BASE_CLASSES = cn('inline-flex items-center justify-center rounded-full', INTERACTIVE_CLASSES);

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
    { variant = 'secondary', size = 'md', loading = false, className, children, ...rest },
    ref,
) {
    return (
        <button
            ref={ref}
            aria-busy={loading || undefined}
            className={cn(BASE_CLASSES, VARIANT_CLASSES[variant], SIZE_CLASSES[size], className)}
            {...rest}
        >
            {loading ? <Spinner className="h-3.5 w-3.5" /> : children}
        </button>
    );
});

export default IconButton;
